import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ShieldCheck, AlertTriangle, XCircle, CheckCircle, RefreshCw,
  Zap, Clock, Database, Users, FileText, Activity, Briefcase
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ENTITY_ICONS = {
  opportunities: Briefcase,
  invoices: FileText,
  accounts: Database,
  contacts: Users,
  sales_orders: Activity,
};

const ENTITY_COLORS = {
  opportunities: 'text-blue-600',
  invoices: 'text-green-600',
  accounts: 'text-purple-600',
  contacts: 'text-teal-600',
  sales_orders: 'text-orange-600',
};

function StatusBadge({ status }) {
  const cfg = {
    ok: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle, label: 'OK' },
    warn: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle, label: 'Warning' },
    critical: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'Critical' },
    error: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'Error' },
    skipped: { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: Clock, label: 'Skipped' },
  }[status] || { color: 'bg-gray-100 text-gray-600', icon: Clock, label: status };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cfg.color}`}>
      <Icon className="h-2.5 w-2.5" />{cfg.label}
    </span>
  );
}

export default function DataIntegrityDashboard() {
  const [health, setHealth] = useState(null);
  const [latestReport, setLatestReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [syncStatus, setSyncStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityDetail, setEntityDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, r, hist, ss] = await Promise.allSettled([
        axios.get(`${API_URL}/api/integrity/health`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/integrity/report/latest`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/integrity/reports?limit=5`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/integrity/sync-status`, { headers: getAuthHeader() }),
      ]);
      if (h.status === 'fulfilled') setHealth(h.value.data);
      if (r.status === 'fulfilled') setLatestReport(r.value.data);
      if (hist.status === 'fulfilled') setHistory(hist.value.data || []);
      if (ss.status === 'fulfilled') setSyncStatus(ss.value.data?.sync_status || []);
    } catch { toast.error('Failed to load integrity data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const triggerReconcile = async (entity = null) => {
    setReconciling(true);
    try {
      if (entity) {
        toast.loading(`Reconciling ${entity}...`);
        const res = await axios.post(
          `${API_URL}/api/integrity/reconcile/${entity}?auto_fix=true`,
          {}, { headers: getAuthHeader() }
        );
        toast.dismiss();
        const r = res.data;
        const fixed = r.ids?.fixed_ghosts || 0;
        toast.success(`${entity}: ${fixed > 0 ? `Fixed ${fixed} ghost records. ` : 'Clean. '}Drift: ${r.checksum?.drift_pct || 0}%`);
        setEntityDetail(r);
      } else {
        toast.loading('Running full reconciliation...');
        await axios.post(`${API_URL}/api/integrity/reconcile?auto_fix=true`, {}, { headers: getAuthHeader() });
        toast.dismiss();
        toast.success('Reconciliation started in background. Refreshing in 10s...');
        setTimeout(load, 10000);
      }
      load();
    } catch (e) {
      toast.dismiss();
      toast.error(`Reconciliation failed: ${e.response?.data?.detail || e.message}`);
    } finally { setReconciling(false); }
  };

  if (loading) return (
    <div className="space-y-4">
      <Skeleton className="h-24 w-full" />
      <div className="grid grid-cols-5 gap-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
    </div>
  );

  const entities = health?.entities || [];
  const overallStatus = health?.overall_status || 'unknown';
  const overallIcon = overallStatus === 'ok' ? CheckCircle : overallStatus === 'warn' ? AlertTriangle : XCircle;
  const OverallIcon = overallIcon;

  return (
    <div className="space-y-5" data-testid="data-integrity-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${overallStatus === 'ok' ? 'bg-emerald-100' : overallStatus === 'warn' ? 'bg-amber-100' : 'bg-red-100'}`}>
            <OverallIcon className={`h-6 w-6 ${overallStatus === 'ok' ? 'text-emerald-600' : overallStatus === 'warn' ? 'text-amber-600' : 'text-red-600'}`} />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[#800000]" />
              Data Integrity Monitor
            </h2>
            <p className="text-xs text-gray-400">
              Compares MongoDB snapshot against Odoo ground truth.
              Last check: {health?.checked_at ? new Date(health.checked_at).toLocaleString() : 'Never'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} className="h-8 text-xs">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => triggerReconcile()} disabled={reconciling}
            className="h-8 text-xs bg-[#800000] hover:bg-[#9a1919] text-white" data-testid="run-reconcile-btn">
            <Zap className="h-3.5 w-3.5 mr-1" /> {reconciling ? 'Running...' : 'Run Full Reconciliation'}
          </Button>
        </div>
      </div>

      {/* Entity Cards (Count Check) + Sync Status */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {entities.map(e => {
          const Icon = ENTITY_ICONS[e.entity] || Database;
          const textColor = ENTITY_COLORS[e.entity] || 'text-gray-600';
          const drift = Math.abs(e.drift || 0);
          const isSelected = selectedEntity === e.entity;
          // Find sync status for this entity
          const syncInfo = syncStatus.find(s => s.entity === e.entity || s.entity === e.entity + 's' || e.entity === s.entity + 's');
          
          return (
            <Card
              key={e.entity}
              className={`cursor-pointer transition-all hover:shadow-md ${isSelected ? 'ring-2 ring-[#800000]' : ''} ${e.status === 'critical' ? 'border-red-300' : e.status === 'warn' ? 'border-amber-300' : ''}`}
              onClick={() => setSelectedEntity(isSelected ? null : e.entity)}
              data-testid={`integrity-card-${e.entity}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Icon className={`h-4 w-4 ${textColor}`} />
                  <StatusBadge status={e.status} />
                </div>
                <p className="text-sm font-semibold text-gray-900">{e.label}</p>
                <div className="mt-1 space-y-0.5">
                  <div className="flex justify-between text-[10px]">
                    <span className="text-gray-400">Odoo</span>
                    <span className="font-bold text-gray-700">{(e.odoo_count || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-[10px]">
                    <span className="text-gray-400">Our DB</span>
                    <span className={`font-bold ${drift > 0 ? (e.status === 'critical' ? 'text-red-600' : 'text-amber-600') : 'text-gray-700'}`}>
                      {(e.db_count || 0).toLocaleString()}
                    </span>
                  </div>
                  {drift > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <AlertTriangle className="h-2.5 w-2.5 text-amber-500" />
                      <span className="text-[10px] text-amber-600 font-semibold">
                        {e.drift > 0 ? '+' : ''}{e.drift} drift ({e.drift_pct}%)
                      </span>
                    </div>
                  )}
                  {/* Last synced */}
                  {syncInfo && (
                    <div className="flex items-center gap-1 mt-1.5 pt-1.5 border-t border-gray-100">
                      <Clock className="h-2.5 w-2.5 text-gray-300" />
                      <span className={`text-[9px] ${syncInfo.status === 'stale' ? 'text-amber-500' : syncInfo.ago_label === 'never' ? 'text-red-400' : 'text-gray-400'}`}>
                        {syncInfo.ago_label === 'never' ? 'Never synced' : `Synced ${syncInfo.ago_label}`}
                      </span>
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-full h-6 text-[10px] mt-2 text-[#800000] hover:bg-[#800000]/5"
                  onClick={(ev) => { ev.stopPropagation(); triggerReconcile(e.entity); }}
                  data-testid={`reconcile-${e.entity}-btn`}
                >
                  <Zap className="h-2.5 w-2.5 mr-1" /> Fix
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Sync Status Summary Row */}
      {syncStatus.length > 0 && (
        <Card className="bg-gray-50/50">
          <CardContent className="p-3">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Clock className="h-3 w-3" /> Last Sync Times
            </p>
            <div className="flex flex-wrap gap-2">
              {syncStatus.map(s => (
                <div key={s.entity} className="flex items-center gap-1.5 text-[10px]">
                  <span className={`w-1.5 h-1.5 rounded-full ${s.status === 'ok' ? 'bg-emerald-400' : s.status === 'stale' ? 'bg-amber-400' : 'bg-red-400'}`} />
                  <span className="text-gray-600 font-medium capitalize">{s.entity}</span>
                  <span className="text-gray-400">{s.ago_label}</span>
                  <span className="text-gray-200">·</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entity Detail Panel */}
      {entityDetail && (
        <Card className="border-[#800000]/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[#800000]" />
                Reconciliation Result: {entityDetail.entity}
              </span>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEntityDetail(null)}>×</Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-xs">
              {/* ID Reconciliation */}
              <div className="p-3 rounded-lg bg-gray-50">
                <p className="font-bold text-gray-500 mb-1">ID Check</p>
                <p>Ghost records: <strong className={entityDetail.ids?.ghost_count > 0 ? 'text-amber-600' : 'text-emerald-600'}>{entityDetail.ids?.ghost_count || 0}</strong></p>
                <p>Fixed: <strong className="text-emerald-600">{entityDetail.ids?.fixed_ghosts || 0}</strong></p>
                <p>Missing: <strong className={entityDetail.ids?.missing_count > 0 ? 'text-red-600' : 'text-emerald-600'}>{entityDetail.ids?.missing_count || 0}</strong></p>
                <StatusBadge status={entityDetail.ids?.status || 'ok'} />
              </div>
              {/* Checksum Validation */}
              <div className="p-3 rounded-lg bg-gray-50">
                <p className="font-bold text-gray-500 mb-1">Checksum (Field Drift)</p>
                <p>Sampled: <strong>{entityDetail.checksum?.sampled || 0}</strong></p>
                <p>Drifted fields: <strong className={entityDetail.checksum?.drifted_fields > 0 ? 'text-amber-600' : 'text-emerald-600'}>{entityDetail.checksum?.drifted_fields || 0}</strong></p>
                <p>Drift %: <strong>{entityDetail.checksum?.drift_pct || 0}%</strong></p>
                <StatusBadge status={entityDetail.checksum?.status || 'ok'} />
              </div>
              {/* Count Check */}
              <div className="p-3 rounded-lg bg-gray-50">
                <p className="font-bold text-gray-500 mb-1">Count Check</p>
                <p>Odoo: <strong>{entityDetail.count?.odoo_count || 0}</strong></p>
                <p>DB: <strong>{entityDetail.count?.db_count || 0}</strong></p>
                <p>Drift: <strong className={Math.abs(entityDetail.count?.drift || 0) > 0 ? 'text-amber-600' : 'text-emerald-600'}>{entityDetail.count?.drift || 0}</strong></p>
                <StatusBadge status={entityDetail.count?.status || 'ok'} />
              </div>
            </div>
            {/* Drift details */}
            {entityDetail.checksum?.sample_drift_details?.length > 0 && (
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Field Drift Samples</p>
                <div className="space-y-1">
                  {entityDetail.checksum.sample_drift_details.map((d, i) => (
                    <div key={i} className="text-[10px] flex gap-2 p-1.5 bg-amber-50 rounded">
                      <span className="text-gray-500 font-mono">ID:{d.id}</span>
                      <span className="font-semibold text-gray-700">{d.field}:</span>
                      <span className="text-red-500 line-through">{d.db}</span>
                      <span className="text-emerald-600">→ {d.odoo}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Latest Report Summary */}
      {latestReport && !latestReport.message && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-400" />
              Last Full Reconciliation
              <StatusBadge status={latestReport.overall_status} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-3 text-xs">
              <div className="text-center p-2 bg-gray-50 rounded">
                <p className="text-lg font-bold text-gray-900">{latestReport.entities_checked}</p>
                <p className="text-gray-400">Entities</p>
              </div>
              <div className="text-center p-2 bg-emerald-50 rounded">
                <p className="text-lg font-bold text-emerald-700">{latestReport.total_ghosts_fixed || 0}</p>
                <p className="text-gray-400">Ghosts Fixed</p>
              </div>
              <div className="text-center p-2 bg-blue-50 rounded">
                <p className="text-lg font-bold text-blue-700">{latestReport.elapsed_seconds?.toFixed(1)}s</p>
                <p className="text-gray-400">Duration</p>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded">
                <p className="text-xs font-medium text-gray-700">{latestReport.run_at ? new Date(latestReport.run_at).toLocaleString() : 'N/A'}</p>
                <p className="text-gray-400">Run At</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History */}
      {history.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Reconciliation History</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-xs">
              <thead><tr className="bg-gray-50 border-b"><th className="text-left px-3 py-2">Run At</th><th className="px-3 py-2">Status</th><th className="text-right px-3 py-2">Ghosts Fixed</th><th className="text-right px-3 py-2">Duration</th></tr></thead>
              <tbody>
                {history.map((r, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono">{r.run_at ? new Date(r.run_at).toLocaleString() : '-'}</td>
                    <td className="px-3 py-2"><StatusBadge status={r.overall_status} /></td>
                    <td className="px-3 py-2 text-right">{r.total_ghosts_fixed || 0}</td>
                    <td className="px-3 py-2 text-right">{r.elapsed_seconds?.toFixed(1)}s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Explanation */}
      <Card className="bg-gray-50 border-dashed">
        <CardContent className="p-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5" /> How Data Integrity Works
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-600">
            <div className="flex items-start gap-2">
              <span className="font-bold text-blue-600 shrink-0">1.</span>
              <p><strong>Count Check</strong> — Compares DB count vs Odoo count. If DB has more records, ghost records may exist.</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-bold text-[#800000] shrink-0">2.</span>
              <p><strong>ID Reconciliation</strong> — Fetches all IDs from Odoo, compares with DB. Ghost records (in DB but not Odoo) are soft-deleted automatically.</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-bold text-emerald-600 shrink-0">3.</span>
              <p><strong>Checksum Validation</strong> — Compares key field values (stage, amount, status) for a sample of records. Detects field drift from missed syncs.</p>
            </div>
          </div>
          <p className="text-[10px] text-gray-400 mt-3">
            Reconciliation runs automatically every 24 hours (nightly). Redis caches query results only — data integrity is maintained by comparing against Odoo's XML-RPC API as the single source of truth.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
